/**
 * RevealWhenVisible
 *
 * Windowed (virtualized) rendering for search-result cards: a card is
 * only mounted while its slot is within the viewport plus a generous
 * overscan margin. Off-screen slots render a fixed-height placeholder,
 * so long result lists keep only the visible cards in the DOM and
 * unrelated re-renders (e.g. map pan/zoom state changes in the parent)
 * never touch off-screen card subtrees (task #8014).
 *
 * - The first card (`eager`) is always rendered immediately and stays
 *   mounted — it is the LCP element on mobile (task #4410) and must not
 *   be deferred or windowed out.
 * - `REVEAL_MARGIN_PX` of overscan (~3 rows) means cards mount well
 *   before the user reaches them and unmount only once well past, so
 *   no skeleton is ever visible during normal scrolling (in either
 *   direction).
 * - When a previously-revealed card is windowed out, its last measured
 *   height is kept as a `minHeight` on the slot so the scroll position
 *   never jumps.
 * - `forceReveal` (selected map pin) mounts the card immediately and
 *   latches it mounted, so the highlight ring and the scroll-to-card
 *   effect always find a real card (task #7138).
 * - Falls back gracefully in environments without IntersectionObserver
 *   (SSR, old browsers) by rendering eagerly and permanently.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSsrBootstrap } from "@/lib/ssr-bootstrap";

interface RevealWhenVisibleProps {
  /** Render eagerly and permanently (use for the first card, LCP). */
  eager?: boolean;
  /**
   * Force the real card to render even if it hasn't scrolled into view.
   * Used when a map pin is selected/hovered so the matching card can show
   * its highlight (and be scrolled to) even while still deferred. Once
   * true, the card latches mounted (exempt from windowing) so the
   * scroll-to-card effect never races an unmount.
   */
  forceReveal?: boolean;
  /** Shown while the card is off-screen. Should be a fixed-height placeholder. */
  skeleton: ReactNode;
  children: ReactNode;
}

// Overscan in px above/below the viewport within which cards stay mounted.
// Large enough (~3 card rows) that neither fast scrolling down nor
// scrolling back up ever shows a skeleton in the visible viewport.
const REVEAL_MARGIN_PX = 600;

export function RevealWhenVisible({ eager = false, forceReveal = false, skeleton, children }: RevealWhenVisibleProps) {
  const ssrBootstrap = useSsrBootstrap();
  // Permanent mounts (exempt from windowing): the eager/LCP card, any card
  // that has ever been force-revealed, and fail-open environments without
  // IntersectionObserver.
  const [forceLatched, setForceLatched] = useState(forceReveal);
  const permanent =
    ssrBootstrap || eager || forceReveal || forceLatched || typeof IntersectionObserver === "undefined";

  const [inWindow, setInWindow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Height of the card the last time it was mounted, used as a minHeight
  // on the placeholder so windowing a card out never shifts the scroll
  // position or collapses the grid row.
  const lastHeightRef = useRef<number | null>(null);

  useEffect(() => {
    if (forceReveal && !forceLatched) setForceLatched(true);
  }, [forceReveal, forceLatched]);

  useEffect(() => {
    if (permanent) return;
    const el = ref.current;
    if (!el) return;
    // Synchronous check: if the element is already within the viewport +
    // overscan at mount time, reveal immediately without waiting for an
    // async IO callback. This eliminates the one-frame skeleton flash for
    // cards that are visible on the initial render.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + REVEAL_MARGIN_PX && rect.bottom > -REVEAL_MARGIN_PX) {
      setInWindow(true);
    }
    // Two-way observer: mount when the slot enters the overscan window,
    // unmount (back to placeholder) when it leaves. The observer stays
    // connected for the component's lifetime so cards window in and out
    // as the user scrolls.
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry.isIntersecting) {
          setInWindow(true);
        } else {
          const h = el.getBoundingClientRect().height;
          if (h > 0) lastHeightRef.current = h;
          setInWindow(false);
        }
      },
      { rootMargin: `${REVEAL_MARGIN_PX}px 0px` },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [permanent]);

  if (permanent) {
    return <>{children}</>;
  }

  // The wrapper persists across mount/unmount cycles so the single
  // IntersectionObserver keeps watching the slot in both states.
  return (
    <div
      ref={ref}
      className="h-full"
      style={!inWindow && lastHeightRef.current ? { minHeight: lastHeightRef.current } : undefined}
    >
      {inWindow ? children : skeleton}
    </div>
  );
}
